import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from '../../components/connectedForm/Dropdown';
import _get from 'lodash/get';

import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import DropdownLatest from '../connectedForm/DropdownLatest';
import MultiQueryDropdown from '../connectedForm/MultiQueryDropdown';
import fieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';

// Defaults to props value but can be overriden
const getValueFromProps = ({ rowData, column, value }: any) => {
  const { getDefaultValue } = column;
  if (getDefaultValue) {
    const defaultValue = getDefaultValue(column, rowData);
    if ((!value || value === 0 || value === '') && defaultValue) return defaultValue;
  }
  return value;
};

/**
 * Note - 'props' is type 'any' because there are (currently) a variety of inner components which could have differing props.
 * (Although they should be mostly similar, could be improved later).
 */
const DropdownEditorClean = forwardRef<any, any>((props, ref) => {
  const { column, value, onCommit, updateCellState, rowData, rowMetaData } = props;
  const { options, key } = column;
  const inputNodeRef = useRef();

  const dropdownOptions = options?.dropdown;
  const rowSelectionKeys = dropdownOptions?.rowSelectionKeys;
  const optionKey = dropdownOptions?.selectionKey ? dropdownOptions?.selectionKey : dropdownOptions?.valueKey;
  const dropdownItems = rowData[dropdownOptions?.options];

  const findMatch = useMemo(() => {
    let match;
    const matchingItems = dropdownItems?.filter((item: { [x: string]: any }) => item[optionKey] === value);
    matchingItems?.forEach((item: { [x: string]: any }) => {
      let isMatch = true;
      rowSelectionKeys?.forEach((selectionKey: string) => {
        if (!(rowData[selectionKey] === item[selectionKey])) {
          isMatch = false;
        }
      });

      if (isMatch && item) {
        match = item;
      }
    });
    return match;
  }, [dropdownItems, optionKey, value, rowSelectionKeys, rowData]);

  const [currentValue, setCurrentValue] = useState(findMatch);

  useEffect(() => {
    if (typeof value === 'object') {
      setCurrentValue(value);
    } else {
      setCurrentValue(findMatch);
    }
  }, [value]);

  const onChange = (
    newValue: any,
    keyCode: any,
    {
      skipTabBehaviour,
      setInactive,
      triggerTemporarySave,
      shift,
    }: { skipTabBehaviour?: boolean; setInactive?: boolean; triggerTemporarySave?: boolean; shift?: boolean } = {}
  ) => {
    let update = newValue;
    if (options?.dropdown) {
      const { isEnum } = options.dropdown;
      if (isEnum) update = newValue.value;
    }
    setCurrentValue(update);

    onCommit(
      { shift: shift },
      { value: getValue(newValue, keyCode), skipTabBehaviour, setInactive, triggerTemporarySave }
    );
    updateCellState({ modalOpen: false });
  };

  const onBlur = (event: any) => {
    props.onBlur?.(event);
  };

  const getOptions = () => {
    if (options?.dropdown) {
      if (typeof options.dropdown.options === 'function') {
        return options.dropdown.options();
      }
      if (options.dropdown.options) {
        return _get(rowData, options.dropdown.options) || [];
      }
      return rowMetaData?.options ? rowMetaData.options : null;
    }
    return null;
  };

  const getValue = (newValue: any, keyCode: any) => {
    const updated: any = {};
    const updatedValue = newValue === '' ? null : newValue;
    updated[key] = updatedValue;

    const { entityType, entityId, entityDisplay } = options;
    updated.entityType = entityType;
    updated.idPath = entityId;
    updated.displayPath = entityDisplay;
    updated.idValue = updatedValue;
    updated.lastKeyCode = keyCode;

    return updated;
  };

  const getValueFromOption = (option: any) => {
    let updatedValue = null;
    if (options?.dropdown) {
      updatedValue = _get(option, options.dropdown.valueField);
    }
    setCurrentValue(updatedValue);
  };

  let editorProps = {};
  if (options?.dropdown) {
    const {
      searchTabOffset,
      searchEnterOffset,
      dropdown: {
        query,
        selectModal,
        valueRenderer,
        optionRenderer,
        filterOption,
        filterOptions,
        remoteFilter,
        valueKey,
        labelKey,
        valueField,
        dataField,
        displayFields,
        freeType,
        isEnum,
        clearable,
        shouldKeyDownEventCreateNewOption,
        newOptionCreator,
        promptTextCreator,
        predefinedFilters,
        creatable,
      },
    } = options;

    const dropdownEditorProps: any = {
      options: getOptions(),
      filterOptions,
      valueField,
      dataField,
      displayFields,
      freeType,
      getValueFromOption,
      inGrid: true,
      valueKey,
      labelKey,
      dataid: key,
      selectModal,
      newOptionCreator,
      clearable,
      shouldKeyDownEventCreateNewOption,
      promptTextCreator,
      searchTabOffset,
      searchEnterOffset,
      creatable,
    };

    if (optionRenderer) {
      dropdownEditorProps.optionRenderer = optionRenderer;
    }
    if (valueRenderer) {
      dropdownEditorProps.valueRenderer = valueRenderer;
    }
    if (query) {
      dropdownEditorProps.options = null;
      dropdownEditorProps.filterOption = filterOption;
      dropdownEditorProps.remoteFilter = remoteFilter;
      dropdownEditorProps.predefinedFilters = predefinedFilters;
    } else if (isEnum) {
      dropdownEditorProps.options = (fieldMetaData[options.type] as IEnumMetaData).enumValues;
    }
    editorProps = dropdownEditorProps;
  }

  //Generate a fake 'component' to pass to EditorContainer.ts so we don't have to alter it too much to cope with functional components.
  const editorRef = useRef<any>({});
  (ref as (el: any) => void)(editorRef.current);
  //Pass some mocks to make EditorContainer.ts play nicely.
  editorRef.current.ignore = true;
  editorRef.current.getValue = () => {};
  editorRef.current.getInputNode = () => inputNodeRef.current;
  editorRef.current.getEditorProps = () => editorProps;

  let EditorComponent: any = Dropdown;
  if (options.dropdown?.enableMulti) {
    EditorComponent = MultiQueryDropdown;
  } else if (options.dropdown?.query) {
    EditorComponent = QueryDropdown;
  } else if (options.dropdown?.useLatestReactSelect) {
    EditorComponent = DropdownLatest;
  }

  return (
    <EditorComponent
      ref={inputNodeRef}
      {...props}
      {...editorProps}
      includeLabel={false}
      onChangeValue={onChange}
      onChange={onChange}
      onBlur={onBlur}
      value={currentValue}
    />
  );
});

export default DropdownEditorClean;
